import axiosIns from "@/axios/defaultAxios";
import { slices } from "@/constants/sliceNames";
import { TaskTypes } from "@/constants/task-types";
import { CameraImage, CameraODImage } from "@/entities/Camera";
import { CameraGroup } from "@/entities/Camera/CameraGroup";
import { errorMessage, successMessage } from "@/store/actions/messages.actions";
import { serializeQueryParams } from "@/utils/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";

const ENDPOINT = "camera-groups";

/*** Get list of camera groups */
export const requestListOfCameraGroups = createAsyncThunk(
  `${slices.cameraGroups}/requestListOfCameraGroups`,
  async (params = { sort: "-updated_at" }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}?${serializeQueryParams(params)}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestListOfCameraGroupsStatus = {
  [requestListOfCameraGroups.fulfilled]: (state, action) => {
    const { current_page, page_limit, records_count, total_pages, data } = action.payload;
    state.cameraGroups = data.map(CameraGroup.fromApi);
    state.currentPage = current_page;
    state.pageLimit = page_limit;
    state.recordsCount = records_count;
    state.totalPages = total_pages;
    state.loading = false;
  },
  [requestListOfCameraGroups.pending]: (state, action) => {
    state.loading = true;
  },
  [requestListOfCameraGroups.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/*** Get camera group by id */
export const requestCameraGroupById = createAsyncThunk(
  `${slices.cameraGroups}/requestCameraGroupById`,
  async ({ id, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${id}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCameraGroupByIdStatus = {
  [requestCameraGroupById.fulfilled]: (state, action) => {
    state.cameraGroup = CameraGroup.fromApi(action.payload);
    state.loading = false;
  },
  [requestCameraGroupById.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCameraGroupById.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/*** Create camera group */
export const requestCreateCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestCreateCameraGroup`,
  async (cameraGroup, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}`, cameraGroup);
      if (response.status === 200) {
        dispatch(successMessage("Camera group created successfully"));
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCreateCameraGroupStatus = {
  [requestCreateCameraGroup.fulfilled]: (state, action) => {
    state.cameraGroups.unshift(CameraGroup.fromApi(action.payload));
    state.loading = false;
  },
  [requestCreateCameraGroup.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCreateCameraGroup.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/*** Update camera group */
export const requestUpdateCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestUpdateCameraGroup`,
  async ({ cameraGroupId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.put(`${ENDPOINT}/${cameraGroupId}`, data);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return { cameraGroupId, data };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestUpdateCameraGroupStatus = {
  [requestUpdateCameraGroup.fulfilled]: (state, action) => {
    const { cameraGroupId, data } = action.payload;
    const index = state.cameraGroups.findIndex((cameraGroup) => cameraGroup.id === cameraGroupId);
    if (index !== -1) {
      state.cameraGroups[index].name = data.name;
      state.cameraGroups[index].description = data.description;
      state.cameraGroups[index].cameras = data.cameras;
    }
    state.loading = false;
  },
  [requestUpdateCameraGroup.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUpdateCameraGroup.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/*** Delete camera group */
export const requestDeleteCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestDeleteCameraGroup`,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.delete(`${ENDPOINT}/${id}`);
      if (response.status === 200) {
        dispatch(successMessage(response.data.data));
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestDeleteCameraGroupStatus = {
  [requestDeleteCameraGroup.fulfilled]: (state, action) => {
    const index = state.cameraGroups.findIndex((cameraGroup) => cameraGroup.id === action.payload.id);
    state.cameraGroups.splice(index, 1);
    state.cameraGroup = new CameraGroup();
    state.loading = false;
  },
  [requestDeleteCameraGroup.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteCameraGroup.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/*** Add camera to camera group */
export const requestAddCameraToCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestAddCameraToCameraGroup`,
  async ({ groupId, cameraId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${groupId}/cameras`, {
        cameraId,
      });
      if (response.status === 200) {
        dispatch(successMessage(response.data.data));
        return {
          groupId,
          cameraId,
        };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestAddCameraToCameraGroupStatus = {
  [requestAddCameraToCameraGroup.fulfilled]: (state, action) => {
    const index = state.cameraGroups.findIndex((cameraGroup) => cameraGroup.id === action.payload.groupId);
    if (index !== -1) state.cameraGroups[index].cameras.push(action.payload.cameraId);
    state.loading = false;
  },
  [requestAddCameraToCameraGroup.pending]: (state, action) => {
    state.loading = true;
  },
  [requestAddCameraToCameraGroup.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/*** Connect to camera group */
export const requestConnectToCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestConnectToCameraGroup`,
  async ({ groupId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${groupId}/cameras/connect`, {
        ...data,
      });
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestConnectToCameraGroupStatus = {
  [requestConnectToCameraGroup.fulfilled]: (state, action) => {
    state.cameraGroup.connected = true;
    state.connection.loading = false;
    state.connection.error = "";
  },
  [requestConnectToCameraGroup.pending]: (state, action) => {
    state.cameraGroup.connected = false;
    state.connection.loading = true;
    state.connection.error = "";
  },
  [requestConnectToCameraGroup.rejected]: (state, action) => {
    state.cameraGroup.connected = false;
    state.connection.loading = false;
    state.connection.error = action.payload;
  },
};

/*** Disconnect from camera group */
export const requestDisconnectFromCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestDisconnectFromCameraGroup`,
  async (groupId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${groupId}/cameras/disconnect`);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return { groupId };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestDisconnectFromCameraGroupStatus = {
  [requestDisconnectFromCameraGroup.fulfilled]: (state, action) => {
    state.cameraGroup.connected = false;
    // update connected in cameraGroups array
    const index = state.cameraGroups.findIndex((cameraGroup) => cameraGroup.id === action.payload.groupId);
    if (index !== -1) state.cameraGroups[index].connected = false;
    state.connection.loading = false;
    state.connection.error = "";
  },
  [requestDisconnectFromCameraGroup.pending]: (state, action) => {
    state.cameraGroup.connected = true;
    state.connection.loading = true;
    state.connection.error = "";
  },
  [requestDisconnectFromCameraGroup.rejected]: (state, action) => {
    state.cameraGroup.connected = true;
    state.connection.loading = false;
    state.connection.error = action.payload;
  },
};

export const requestCameraGroupFrames = createAsyncThunk(
  `${slices.cameraGroups}/requestCameraGroupFrames`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosIns.get(
        `${ENDPOINT}/${data.groupId}/cameras/frames?${serializeQueryParams(data.filters)}`
      );
      return { res: res.data.data, filters: data.filters };
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCameraGroupFramesStatus = {
  [requestCameraGroupFrames.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCameraGroupFrames.fulfilled]: (state, { payload }) => {
    const { res, filters } = payload;
    state.loading = false;

    state.frames = res.data?.map((item) => ({
      ...item,
      images: item.images.map((image) =>
        image.type === TaskTypes.OBJECT_DETECTION ? CameraODImage.fromApi(image) : CameraImage.fromApi(image)
      ),
    }));
    const { current_page, page_limit, records_count, total_pages } = res;

    state.currentPage = current_page;
    state.pageLimit = page_limit;
    state.recordsCount = records_count;
    state.totalPages = total_pages;
    state.appliedFiltersOnFrames = filters;
  },

  [requestCameraGroupFrames.rejected]: (state, action) => {
    state.loading = false;
  },
};

export const requestCameraGroupFrameDetails = createAsyncThunk(
  `${slices.cameraGroups}/requestCameraGroupFrameImages`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosIns.get(
        `${ENDPOINT}/${data.groupId}/cameras/images?${serializeQueryParams(data.filters)}`
      );
      return res.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCameraGroupFrameImagesStatus = {
  [requestCameraGroupFrameDetails.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCameraGroupFrameDetails.fulfilled]: (state, { payload }) => {
    state.loading = false;
    state.cameraGroupDetails = payload.data?.map((image) =>
      image.type === TaskTypes.OBJECT_DETECTION ? CameraODImage.fromApi(image) : CameraImage.fromApi(image)
    );
  },

  [requestCameraGroupFrameDetails.rejected]: (state, action) => {
    state.loading = false;
  },
};

/** Get number of images camera group */

export const requestNumOfImagesInCameraGroup = createAsyncThunk(
  `${slices.cameraGroups}/requestNumOfImagesInCameraGroup`,
  async ({ groupId, modelType }, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        image_type: modelType,
      };
      const response = await axiosIns.get(`${ENDPOINT}/${groupId}/images/count?${serializeQueryParams(params)}`);
      if (response.status === 200) {
        return { groupId, size: response.data.data };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestNumOfImagesInCameraGroupStatus = {
  [requestNumOfImagesInCameraGroup.fulfilled]: (state, action) => {
    const { groupId, size } = action.payload;
    const index = state.cameraGroups.findIndex((cameraGroup) => cameraGroup.id === groupId);
    if (index !== -1) state.cameraGroups[index].size = size;
    state.cameraGroupSize = size;
  },
};
